import React from 'react';

import { formatBranding } from '../../utils';

interface RetziaRichMessageProps {
  content: string;
}

const renderInline = (
  text: string
): React.ReactNode => {
  const tokens = text.split(
    /(_{3,}|\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|_[^_]+_)/
  );

  return tokens.map((part, index) => {
    if (/^_{3,}$/.test(part)) {
      const width = Math.min(
        116,
        Math.max(48, part.length * 11)
      );

      return (
        <span
          key={index}
          className="retzia-answer-blank"
          style={{ width }}
          role="img"
          aria-label="Espacio para responder"
        />
      );
    }

    if (
      part.startsWith('***') &&
      part.endsWith('***') &&
      part.length > 6
    ) {
      return (
        <strong key={index}>
          <em>{part.slice(3, -3)}</em>
        </strong>
      );
    }

    if (
      part.startsWith('**') &&
      part.endsWith('**') &&
      part.length > 4
    ) {
      return (
        <strong key={index}>
          {part.slice(2, -2)}
        </strong>
      );
    }

    if (
      part.startsWith('*') &&
      part.endsWith('*') &&
      part.length > 2
    ) {
      return (
        <em key={index}>
          {part.slice(1, -1)}
        </em>
      );
    }

    if (
      part.startsWith('__') &&
      part.endsWith('__') &&
      part.length > 4
    ) {
      return (
        <strong key={index}>
          {part.slice(2, -2)}
        </strong>
      );
    }

    if (
      part.startsWith('_') &&
      part.endsWith('_') &&
      part.length > 2
    ) {
      return (
        <em key={index}>
          {part.slice(1, -1)}
        </em>
      );
    }

    return part;
  });
};

type SectionKind =
  | 'rule'
  | 'examples'
  | 'practice'
  | 'note'
  | 'default';

const normalizeHeading = (
  text: string
): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const sectionKind = (
  title: string
): SectionKind => {
  const normalized = normalizeHeading(title);

  if (/regla|estructura|formula/.test(normalized)) {
    return 'rule';
  }

  if (/ejemplo|examples?|beispiel|exemples?/.test(normalized)) {
    return 'examples';
  }

  if (/ejercicio|practica|practice|ubung|exercice/.test(normalized)) {
    return 'practice';
  }

  if (/ojo|recuerda|tip|nota|achtung|merke|attention/.test(normalized)) {
    return 'note';
  }

  return 'default';
};

const sectionIcon: Record<SectionKind, string> = {
  rule: '✦',
  examples: '',
  practice: '✓',
  note: '!',
  default: '•',
};

const stripInvisibleMarkdownCharacters = (
  text: string
): string =>
  text.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '');

const isEmptyMarkdownHeading = (
  line: string
): boolean =>
  /^#{1,6}$/.test(
    stripInvisibleMarkdownCharacters(line).trim()
  );

const extractHeading = (
  line: string
): string | null => {
  const markdown = line.match(
    /^\s*#{1,6}\s+(.+)\s*$/
  );

  if (markdown) {
    return markdown[1];
  }

  const namedSection = line.match(
    /^\s*(?:\d+\.\s*)?(Regla|Estructura|Fórmula|Ejemplos?|Ejercicios?|Práctica|Ojo|Recuerda|Nota|Examples?|Practice|Tip|Beispiele?|Übungen?|Merke|Exemples?|Exercices?|Attention)\s*:?\s*$/i
  );

  return namedSection ? line.trim() : null;
};

const renderSectionHeading = (
  title: string,
  key: React.Key
) => {
  const kind = sectionKind(title);
  const cleanTitle = title.replace(
    /^\d+\.\s*/,
    ''
  );

  return (
    <span
      key={key}
      className={`retzia-section-heading is-${kind}`}
    >
      {sectionIcon[kind] && (
        <span
          className="retzia-section-icon"
          aria-hidden="true"
        >
          {sectionIcon[kind]}
        </span>
      )}

      <span>{renderInline(cleanTitle)}</span>
    </span>
  );
};

const tableCells = (
  line: string
): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());

const isTableDivider = (
  line: string
): boolean => {
  const cells = tableCells(line);

  return (
    cells.length > 0 &&
    cells.every(cell =>
      /^:?-{3,}:?$/.test(cell)
    )
  );
};

export const RetziaRichMessage = ({
  content,
}: RetziaRichMessageProps) => {
  let text = formatBranding(content);

  text = stripInvisibleMarkdownCharacters(text);
  text = text.replace(/\\n\\n/g, '\n\n');
  text = text.replace(/\\n/g, '\n');
  text = text.replace(/^---+\s*$/gm, '');
  text = text.replace(/^[ \t]*#{1,6}[ \t]*$/gm, '');

  text = text
    .split('\n')
    .map(line => {
      if (/^\s*-\s/.test(line)) {
        const parts = line.split(/ - /);

        if (parts.length > 1) {
          return [
            parts[0],
            ...parts
              .slice(1)
              .map(part => `- ${part}`),
          ].join('\n');
        }
      }

      return line;
    })
    .join('\n');

  text = text.replace(
    / (\d{1,2}\.) /g,
    '\n$1 '
  );

  text = text
    .split('\n')
    .map(line =>
      extractHeading(line)
        ? `\n${line}\n`
        : line
    )
    .join('\n');

  const blocks = text
    .split(/\n\n+/)
    .filter(block => block.trim());

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block
          .split('\n')
          .filter(
            line =>
              line.trim() &&
              !isEmptyMarkdownHeading(line)
          );

        if (lines.length === 0) {
          return null;
        }

        const standaloneHeading =
          lines.length === 1
            ? extractHeading(lines[0])
            : null;

        if (standaloneHeading) {
          return renderSectionHeading(
            standaloneHeading,
            blockIndex
          );
        }

        if (
          lines.length >= 2 &&
          lines[0].includes('|') &&
          isTableDivider(lines[1])
        ) {
          const headers = tableCells(lines[0]);
          const rows = lines
            .slice(2)
            .filter(line => line.includes('|'))
            .map(tableCells);

          return (
            <div
              className="retzia-table-wrap"
              key={blockIndex}
            >
              <table className="retzia-message-table">
                <thead>
                  <tr>
                    {headers.map((header, index) => (
                      <th key={index}>
                        {renderInline(header)}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {headers.map((_, cellIndex) => (
                        <td key={cellIndex}>
                          {renderInline(
                            row[cellIndex] ?? ''
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (
          lines.every(line =>
            /^\s*-\s/.test(line)
          )
        ) {
          return (
            <ul key={blockIndex}>
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>
                  {renderInline(
                    line.replace(/^\s*-\s+/, '')
                  )}
                </li>
              ))}
            </ul>
          );
        }

        if (
          lines.length > 1 &&
          lines.every(line =>
            /^\s*\d+\.\s/.test(line)
          )
        ) {
          return (
            <ol key={blockIndex}>
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>
                  {renderInline(
                    line.replace(/^\s*\d+\.\s+/, '')
                  )}
                </li>
              ))}
            </ol>
          );
        }

        return (
          <p key={blockIndex}>
            {lines.map((line, lineIndex) => {
              const heading = extractHeading(line);

              return (
                <React.Fragment key={lineIndex}>
                  {lineIndex > 0 && <br />}

                  {heading ? (
                    renderSectionHeading(
                      heading,
                      `heading-${lineIndex}`
                    )
                  ) : (
                    renderInline(line)
                  )}
                </React.Fragment>
              );
            })}
          </p>
        );
      })}
    </>
  );
};

export default RetziaRichMessage;
